import { jsx } from "react/jsx-runtime";
import {
  Box,
  Vec,
  getPointerInfo,
  isAccelKey,
  normalizeWheel,
  setPointerCapture,
  useContainer,
  useColorMode,
  useEditor,
  useValue
} from "@tldraw/editor";
import * as React from "react";
import { useTranslation } from "../../hooks/useTranslation/useTranslation.mjs";
import { MinimapManager } from "./MinimapManager.mjs";
const CLICK_JITTER_THRESHOLD_SQ = 4;
function DefaultMinimap() {
  const editor = useEditor();
  const container = useContainer();
  const msg = useTranslation();
  const rCanvas = React.useRef(null);
  const rPointing = React.useRef(false);
  const rActivePointerId = React.useRef(null);
  const rOriginScreenPoint = React.useRef(new Vec());
  const minimapRef = React.useRef(void 0);
  React.useEffect(() => {
    try {
      const minimap = new MinimapManager(editor, rCanvas.current, container);
      minimapRef.current = minimap;
      return minimapRef.current.close;
    } catch (e) {
      editor.annotateError(e, {
        origin: "minimap",
        willCrashApp: false
      });
      editor.timers.setTimeout(() => {
        throw e;
      });
      return void 0;
    }
  }, [editor, container]);
  const onDoubleClick = React.useCallback(
    (e) => {
      if (!editor.getCurrentPageShapeIds().size) return;
      const minimap = minimapRef.current;
      if (!minimap) return;
      const { clientX: x, clientY: y } = e;
      const clampedPoint = minimap.minimapScreenPointToPagePoint(x, y, false, true);
      minimap.originPagePoint.setTo(clampedPoint);
      minimap.originPageCenter.setTo(editor.getViewportPageBounds().center);
      const point = minimap.minimapScreenPointToPagePoint(x, y, false, false);
      editor.centerOnPoint(point, { animation: { duration: editor.options.animationMediumMs } });
    },
    [editor]
  );
  const onPointerDown = React.useCallback(
    (e) => {
      const minimap = minimapRef.current;
      if (!minimap) return;
      if (e.button !== 0) return;
      const elm = e.currentTarget;
      setPointerCapture(elm, e);
      if (!editor.getCurrentPageShapeIds().size) return;
      const { clientX: x, clientY: y } = e;
      rPointing.current = true;
      rActivePointerId.current = e.pointerId;
      rOriginScreenPoint.current = new Vec(x, y);
      minimap.isInViewport = false;
      const point = minimap.minimapScreenPointToPagePoint(x, y, false, false);
      const vpPageBounds = editor.getViewportPageBounds();
      const commonBounds = minimap.getContentPageBounds();
      const allowedBounds = new Box(
        commonBounds.x - vpPageBounds.width / 2,
        commonBounds.y - vpPageBounds.height / 2,
        commonBounds.width + vpPageBounds.width,
        commonBounds.height + vpPageBounds.height
      );
      if (allowedBounds.containsPoint(point) && !vpPageBounds.containsPoint(point)) {
        const delta = Vec.Sub(vpPageBounds.center, vpPageBounds.point);
        const pagePoint = Vec.Add(point, delta);
        minimap.originPagePoint.setTo(pagePoint);
        minimap.originPageCenter.setTo(point);
        editor.centerOnPoint(point, { animation: { duration: editor.options.animationMediumMs } });
      } else {
        const clampedPoint = minimap.minimapScreenPointToPagePoint(x, y, false, true);
        minimap.isInViewport = vpPageBounds.containsPoint(clampedPoint);
        minimap.originPagePoint.setTo(clampedPoint);
        minimap.originPageCenter.setTo(vpPageBounds.center);
      }
      const body = editor.getContainerDocument().body;
      function endDrag() {
        if (rActivePointerId.current !== null && elm.hasPointerCapture(rActivePointerId.current)) {
          elm.releasePointerCapture(rActivePointerId.current);
        }
        rPointing.current = false;
        rActivePointerId.current = null;
        body.removeEventListener("pointerup", endDrag);
        body.removeEventListener("pointercancel", endDrag);
        body.removeEventListener("contextmenu", endDrag, true);
      }
      body.addEventListener("pointerup", endDrag);
      body.addEventListener("pointercancel", endDrag);
      body.addEventListener("contextmenu", endDrag, true);
    },
    [editor]
  );
  const onPointerMove = React.useCallback(
    (e) => {
      const minimap = minimapRef.current;
      if (!minimap) return;
      const { clientX: x, clientY: y } = e;
      const point = minimap.minimapScreenPointToPagePoint(x, y, e.shiftKey, true);
      if (rPointing.current) {
        if (Vec.Dist2(rOriginScreenPoint.current, new Vec(x, y)) <= CLICK_JITTER_THRESHOLD_SQ) {
          return;
        }
        if (minimap.isInViewport) {
          const delta = minimap.originPagePoint.clone().sub(minimap.originPageCenter);
          editor.centerOnPoint(Vec.Sub(point, delta));
          return;
        }
        editor.centerOnPoint(point);
      }
      const pagePoint = minimap.getMinimapPagePoint(x, y);
      const screenPoint = editor.pageToScreen(pagePoint);
      const info = {
        type: "pointer",
        target: "canvas",
        name: "pointer_move",
        ...getPointerInfo(editor, e),
        point: screenPoint,
        isPen: editor.getInstanceState().isPenMode
      };
      editor.dispatch(info);
    },
    [editor]
  );
  const onWheel = React.useCallback(
    (e) => {
      const offset = normalizeWheel(e);
      editor.dispatch({
        type: "wheel",
        name: "wheel",
        delta: offset,
        point: new Vec(e.clientX, e.clientY),
        shiftKey: e.shiftKey,
        altKey: e.altKey,
        ctrlKey: e.metaKey || e.ctrlKey,
        metaKey: e.metaKey,
        accelKey: isAccelKey(e)
      });
    },
    [editor]
  );
  const colorMode = useColorMode();
  const currentThemeId = useValue("current theme id", () => editor.getCurrentThemeId(), [editor]);
  React.useEffect(() => {
    editor.timers.setTimeout(() => {
      minimapRef.current?.updateColors();
      minimapRef.current?.render();
    });
  }, [colorMode, currentThemeId, editor]);
  return /* @__PURE__ */ jsx("div", { className: "tlui-minimap", children: /* @__PURE__ */ jsx(
    "canvas",
    {
      ref: rCanvas,
      role: "img",
      "aria-label": msg("navigation-zone.minimap"),
      "data-testid": "minimap.canvas",
      className: "tlui-minimap__canvas",
      onDoubleClick,
      onPointerMove,
      onPointerDown,
      onWheelCapture: onWheel
    }
  ) });
}
export {
  DefaultMinimap
};
//# sourceMappingURL=DefaultMinimap.mjs.map
